import { format } from "date-fns";
import { Fragment, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";
import { PublicSession } from "@classview/shared-types";

import { ScrollArea } from "./ui/scroll-area";

type DayCalendarProps = {
  sessions: PublicSession[];
  /**
   * If the date is today, we want to highlight the current time line
   */
  isToday: boolean;
};
export function DayCalendar(props: DayCalendarProps) {
  const { sessions, isToday } = props;
  const MINUTES_IN_DAY = 1440; // 24 hours * 60 minutes
  const OFFSET_HEIGHT = 28; // h-7
  const container = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<HTMLDivElement>(null);

  // Generate array of hours for the day
  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 || 12;
    const period = i < 12 ? "AM" : "PM";
    return `${hour}${period}`;
  });

  useEffect(() => {
    const updateCurrentTimeLine = () => {
      if (!currentTimeRef.current) return;

      const now = new Date();
      const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes();
      /**
       * every hour split into 2 rows
       * every row is 3.75rem which is 60px
       * so every 2px is 1 minute
       */
      const position = minutesSinceMidnight * 2 + OFFSET_HEIGHT;

      currentTimeRef.current.style.top = `${position}px`;
    };

    // Initial position
    updateCurrentTimeLine();

    // Update position every minute
    const interval = setInterval(updateCurrentTimeLine, 60000);

    // Set initial scroll position
    const currentMinute = new Date().getHours() * 60;
    if (container.current) {
      container.current.scrollTop =
        ((container.current.scrollHeight - OFFSET_HEIGHT) * currentMinute) /
        MINUTES_IN_DAY;
    }

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <ScrollArea
      type="always"
      className="isolate flex h-[600px] rounded-lg border border-border"
      ref={container}
    >
      <div className="flex w-full flex-auto">
        <div className="w-14 flex-none bg-card ring-1 ring-border" />
        <div className="grid flex-auto grid-cols-1 grid-rows-1">
          {/* Add current time line */}
          <div
            className={cn(
              "relative col-start-1 col-end-2 row-start-1 row-end-2",
              !isToday && "hidden",
            )}
          >
            <div
              ref={currentTimeRef}
              className="absolute left-0 right-0 z-30 flex items-center"
              style={{ height: "2px" }}
            >
              <div className="-ml-1 h-2 w-2 rounded-full bg-primary" />
              <div className="h-[2px] flex-1 bg-primary" />
            </div>
          </div>

          {/* Horizontal lines */}
          <div
            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-border"
            style={{ gridTemplateRows: "repeat(48, minmax(3.75rem, 1fr))" }}
          >
            <div className="row-end-1 h-7"></div>
            {hours.map((hour, index) => (
              <Fragment key={`hour-${index}`}>
                <div key={`hour-${index}`}>
                  <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                    {hour}
                  </div>
                </div>
                <div key={`spacer-${index}`} />
              </Fragment>
            ))}
          </div>

          {/* Sessions */}
          <ol
            className="z-20 col-start-1 col-end-2 row-start-1 mr-2 grid grid-cols-1"
            style={{
              gridTemplateRows: "1.75rem repeat(288, minmax(0, 1fr)) auto",
            }}
          >
            {sessions.map((session) => {
              const startDate = new Date(session.start);
              const endDate = new Date(session.end);
              const startMinutes =
                startDate.getHours() * 60 + startDate.getMinutes();
              const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();
              const startRow = Math.floor(startMinutes / 5) + 2; // +2 for header row and 1-based grid
              const durationRows = Math.ceil((endMinutes - startMinutes) / 5);
              const gridRow = `${startRow} / span ${durationRows}`;
              return (
                <li
                  key={session.id}
                  className="relative mt-px flex"
                  style={{ gridRow }}
                >
                  <Link
                    to={`/sessions/${session.id}`}
                    className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg border border-primary-200 bg-primary-foreground p-2 text-xs/5 transition-colors hover:cursor-pointer hover:border-primary"
                  >
                    <p className="order-1 font-semibold text-primary">
                      {session.name}
                    </p>
                    <p className="text-primary group-hover:text-primary">
                      <time dateTime={session.start}>
                        {format(new Date(session.start), "h:mm a")}
                      </time>
                    </p>
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </ScrollArea>
  );
}
